import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Download, Mail, Phone, FileText, Lightbulb,
  ShieldCheck, Network, GitBranch, Factory, Microscope, Server, KeyRound, Workflow, Star,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LICENSING_PATENTS } from '../data/licensingPatents';

const PATENT_ICONS = {
  'patent-1-board-reliability':    ShieldCheck,
  'patent-2-ethernet-profiles':    Network,
  'patent-3-supply-chain-graph':   GitBranch,
  'patent-4-mfg-stability':        Factory,
  'patent-5-ai-lithography':       Microscope,
  'patent-6-ai-infrastructure':    Server,
  'patent-7-pq-provenance':        KeyRound,
  'patent-8-agentic-settlement-governor': Workflow,
};

/* Stack visual: 8 layers, top → bottom = P8 → P1. */
const STACK_LAYERS = [
  { num: 8, label: 'Agentic Action Settlement / Decision Governance', slug: 'patent-8-agentic-settlement-governor', accent: 'from-amber-300 to-cyan-300', apex: true },
  { num: 7, label: 'Quantum-Safe Provenance',                         slug: 'patent-7-pq-provenance',              accent: 'from-cyan-400 to-blue-400' },
  { num: 6, label: 'AI Infrastructure Power-Thermal Assurance',       slug: 'patent-6-ai-infrastructure',          accent: 'from-blue-400 to-indigo-400' },
  { num: 5, label: 'Lithography / Process Assurance',                 slug: 'patent-5-ai-lithography',             accent: 'from-indigo-400 to-violet-400' },
  { num: 4, label: 'Manufacturing Stability',                         slug: 'patent-4-mfg-stability',              accent: 'from-violet-400 to-fuchsia-400' },
  { num: 3, label: 'Supply-Chain Risk Intelligence',                  slug: 'patent-3-supply-chain-graph',         accent: 'from-fuchsia-400 to-rose-400' },
  { num: 2, label: 'Connectivity / Interconnect Governance',          slug: 'patent-2-ethernet-profiles',          accent: 'from-rose-400 to-amber-400' },
  { num: 1, label: 'Board Reliability',                               slug: 'patent-1-board-reliability',          accent: 'from-amber-400 to-emerald-400' },
];

const FAQS = [
  {
    q: 'What licensing structures do you offer?',
    a: 'Non-exclusive field-of-use is the most common structure (royalty or one-time fee). We also evaluate exclusive within a field of use, joint development agreements (including co-bidding on SBIR/STTR/OTA/BAA), option / right-of-first-refusal, portfolio cross-license bundles, and strategic acquisition for qualified buyers.',
  },
  {
    q: 'Can I get the full licensing brief?',
    a: 'The full 9–10 page licensing brief is NDA-only and delivered by email within two business days after a one-page mutual NDA is executed. The public 1-page glossy for each patent is downloadable from each per-patent page.',
  },
  {
    q: 'How does the engagement process work?',
    a: 'Step 1: submit a request below or email licensing@salartechnologies.com. Step 2: SALAR returns a one-page mutual NDA within two business days. Step 3: 30-minute briefing call. Step 4: SALAR returns a high-level applicability map within five business days indicating which features address which of your use cases. Step 5: term sheet.',
  },
  {
    q: 'Who currently holds the rights?',
    a: 'SALAR Technologies LLC holds all rights to the eight pending US utility patent applications free of encumbrances. The inventor is Vikrant Salar, Founder & Principal.',
  },
  {
    q: 'Can we license a bundle?',
    a: 'Yes. Multi-patent cross-license bundles are available with quantity discounts — for example, a Defense bundle (P3 + P7), an AI Infrastructure bundle (P2 + P6), a Trusted-Manufacturing bundle (P4 + P5 + P7), or an Accountable AI bundle (P7 + P8).',
  },
  {
    q: 'Is the technology export-controlled?',
    a: 'The patent applications themselves are US public filings. Any deployed implementation must be evaluated for ITAR/EAR applicability case-by-case; SALAR will surface relevant considerations during the briefing call.',
  },
];

const Licensing = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Patent Licensing | SALAR Technologies',
    url: 'https://www.salartechnologies.com/licensing',
    inLanguage: 'en-US',
    isPartOf: { '@type': 'WebSite', name: 'SALAR Technologies LLC', url: 'https://www.salartechnologies.com/' },
    about: {
      '@type': 'Organization',
      name: 'SALAR Technologies LLC',
      url: 'https://www.salartechnologies.com/',
      founder: { '@type': 'Person', name: 'Vikrant Salar', jobTitle: 'Founder & Principal' },
      address: { '@type': 'PostalAddress', addressLocality: 'Wilsonville', addressRegion: 'OR', addressCountry: 'US' },
      identifier: [
        { '@type': 'PropertyValue', propertyID: 'UEI',  value: 'U4TVNBSBUJP3' },
        { '@type': 'PropertyValue', propertyID: 'CAGE', value: '1AXR9' },
      ],
    },
    mainEntity: LICENSING_PATENTS.map((p) => ({
      '@type': 'CreativeWork',
      name: p.title,
      identifier: `U.S. Patent Application No. ${p.applicationNumber}`,
      creator: { '@type': 'Person', name: 'Vikrant Salar' },
      provider: { '@type': 'Organization', name: 'SALAR Technologies LLC' },
      additionalType: 'Patent application — Filed; Patent Pending',
      url: `https://www.salartechnologies.com/licensing/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-blue-950/20">
      <Helmet>
        <title>Patent Licensing | SALAR Technologies</title>
        <meta name="description" content="License SALAR Technologies' ten patent filings across high-speed validation, fault-tolerant quantum hardware assurance, agentic decision governance, quantum-safe provenance, AI infrastructure, process assurance, manufacturing stability, supply-chain intelligence, interconnect governance, and board reliability." />
        <meta name="keywords" content="patent licensing, post-quantum cryptography, AI infrastructure, semiconductor supply chain, manufacturing stability, lithography AI, Ethernet SoC, board reliability, CHIPS Act, CNSA 2.0, NSM-10, SBIR partnership" />
        <meta property="og:title" content="Patent Licensing | SALAR Technologies" />
        <meta property="og:description" content="Ten pending US patent applications forming a verifiable-resilience platform. Non-exclusive, exclusive, joint-development, cross-license, and strategic-acquisition structures available." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.salartechnologies.com/licensing" />
        <link rel="canonical" href="https://www.salartechnologies.com/licensing" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ===== HERO ===== */}
      <section className="bg-gradient-to-b from-[#0a1628] via-[#0e1f3a] to-[#0a1628] border-b border-blue-800/40 py-16 lg:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-cyan-300 text-xs font-bold tracking-[0.18em] uppercase mb-4">
              <Lightbulb size={14} /> SALAR Technologies LLC · Patent Licensing
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
              Ten patent areas.<br className="hidden sm:block" />
              <span className="text-cyan-300">One verifiable resilience platform.</span>
            </h1>
            <p className="text-base lg:text-lg text-blue-100/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              Pending US patent applications spanning board reliability, interconnect governance, semiconductor
              supply-chain graph intelligence, manufacturing stability, process assurance, AI infrastructure
              power-thermal assurance, quantum-safe provenance, and agentic decision governance. Non-exclusive, exclusive, joint-development,
              cross-license, and strategic-acquisition structures available.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/licensing/request" data-testid="licensing-cta-request">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold px-6 py-6 text-base">
                  Request a Briefing <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="#portfolio" data-testid="licensing-cta-portfolio">
                <Button variant="outline" className="border-cyan-400 text-cyan-100 hover:bg-cyan-400/10 px-6 py-6 text-base">
                  View the Portfolio
                </Button>
              </a>
              <a href="mailto:licensing@salartechnologies.com" data-testid="licensing-cta-email">
                <Button variant="outline" className="border-blue-400/60 text-blue-100 hover:bg-blue-500/10 px-6 py-6 text-base">
                  <Mail className="mr-2" size={18} /> licensing@salartechnologies.com
                </Button>
              </a>
            </div>

            <div className="mt-10 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-xs text-blue-200/80 font-mono">
              <span><span className="text-blue-300/70">UEI</span> U4TVNBSBUJP3</span>
              <span className="hidden sm:inline text-blue-400/30">·</span>
              <span><span className="text-blue-300/70">CAGE</span> 1AXR9</span>
              <span className="hidden sm:inline text-blue-400/30">·</span>
              <span><span className="text-blue-300/70">SAM</span> Active</span>
              <span className="hidden sm:inline text-blue-400/30">·</span>
              <span><span className="text-blue-300/70">Oregon LLC</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 8-LAYER STACK VISUAL ===== */}
      <section className="py-16 bg-[#070f1c]" data-testid="licensing-stack">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-3">The Resilience Stack</h2>
              <p className="text-blue-100 leading-relaxed max-w-2xl mx-auto">
                Each layer is a separate pending US patent application. Together they form a single
                cryptographically-anchored, evidence-first stack for AI-era infrastructure, defense, commercial, industrial, and consumer electronics ecosystems.
              </p>
              <p className="text-cyan-200 text-sm mt-4">
                Eight public licensing pages are represented in the current licensing stack; SALAR’s broader public portfolio is described as ten patent filings.
              </p>
            </div>
            <div className="space-y-2">
              {STACK_LAYERS.map((layer, i) => (
                <Link
                  key={layer.num}
                  to={`/licensing/${layer.slug}`}
                  className="group block"
                  data-testid={`licensing-stack-${layer.num}`}
                >
                  <div
                    className={[
                      'relative flex items-center gap-4 rounded-md border border-blue-700/40 bg-blue-950/40',
                      'px-4 py-3 sm:px-6 sm:py-4 transition-all',
                      'hover:border-cyan-400/60 hover:bg-blue-950/70 hover:translate-x-1',
                      layer.apex ? 'ring-1 ring-cyan-400/30 border-cyan-400/40' : '',
                    ].join(' ')}
                    style={{
                      // taper from 92% width at apex to 100% at base, like a layered cake
                      marginLeft:  `${(i / (STACK_LAYERS.length - 1)) * 4}%`,
                      marginRight: `${(i / (STACK_LAYERS.length - 1)) * 4}%`,
                    }}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-gradient-to-br ${layer.accent} text-[#0a1628] font-bold flex items-center justify-center text-base sm:text-lg shadow-md`}>
                      P{layer.num}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold leading-tight ${layer.apex ? 'text-white text-base sm:text-lg' : 'text-blue-50 text-sm sm:text-base'}`}>
                        {layer.label}
                      </p>
                      {layer.apex && (
                        <p className="text-[11px] uppercase tracking-widest text-cyan-300 mt-1">Apex · Post-Quantum Trust</p>
                      )}
                    </div>
                    <ArrowRight size={16} className="flex-shrink-0 text-blue-300 group-hover:text-cyan-300 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PORTFOLIO GRID ===== */}
      <section id="portfolio" className="py-16" data-testid="licensing-portfolio">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3">The Patent Portfolio</h2>
            <p className="text-blue-100 leading-relaxed mb-8 max-w-3xl">
              Click any patent for the public 1-page summary, downloadable glossy PDF, and a path to request the full
              NDA-only licensing brief.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {LICENSING_PATENTS.map((p) => {
                const Icon = PATENT_ICONS[p.slug] || FileText;
                const isFlagship = !!p.flagship;
                return (
                  <Link
                    key={p.slug}
                    to={`/licensing/${p.slug}`}
                    className="group block"
                    data-testid={`licensing-card-${p.slug}`}
                  >
                    <Card
                      className={[
                        'h-full transition-all backdrop-blur-sm',
                        isFlagship
                          ? 'border-cyan-400/70 bg-gradient-to-br from-blue-900/50 to-indigo-900/30 ring-1 ring-cyan-400/30 group-hover:ring-cyan-400/60'
                          : 'border-blue-700/40 bg-blue-950/30 group-hover:border-cyan-400/50 group-hover:translate-y-[-2px]',
                      ].join(' ')}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-xs text-cyan-300/90 font-bold tracking-widest uppercase">
                            <Icon size={14} /> Patent {p.num}
                          </div>
                          {isFlagship && (
                            <Badge variant="outline" className="border-cyan-400 text-cyan-200 bg-cyan-500/10">
                              <Star size={11} className="mr-1 inline" /> FLAGSHIP
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-white text-base lg:text-lg leading-tight">
                          {p.shortTitle}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-blue-100/90 leading-snug">{p.tagline}</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-mono text-cyan-200">{p.applicationNumber}</span>
                          <Badge variant="outline" className="border-emerald-400/40 text-emerald-200/90 bg-emerald-500/5">
                            Filed · Patent Pending
                          </Badge>
                        </div>
                        <div className="pt-1 inline-flex items-center text-xs text-cyan-300 group-hover:text-cyan-200 font-semibold">
                          Read details <ArrowRight size={12} className="ml-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-16 bg-blue-950/20 border-y border-blue-800/30" data-testid="licensing-faq">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3">Licensing FAQ</h2>
            <p className="text-blue-100 leading-relaxed mb-8">
              Quick answers to the questions we hear most often from defense, semiconductor, AI infrastructure, and
              cloud partners.
            </p>
            <div className="space-y-3">
              {FAQS.map((item, i) => (
                <details
                  key={i}
                  className="group rounded-lg border border-blue-700/40 bg-blue-950/30 px-4 py-3 hover:border-cyan-400/50 transition-colors"
                  data-testid={`licensing-faq-${i}`}
                >
                  <summary className="flex items-center justify-between cursor-pointer list-none text-blue-50 font-semibold">
                    {item.q}
                    <span className="ml-3 text-cyan-300 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-blue-100/90 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER CTA ===== */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to talk?</h2>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              The fastest path is the request form — it pre-routes to the right inquiry queue and triggers the
              one-page mutual NDA within two business days.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/licensing/request" data-testid="licensing-footer-request">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold px-6 py-6 text-base">
                  Request a Briefing <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="mailto:licensing@salartechnologies.com" data-testid="licensing-footer-email">
                <Button variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-400/10 px-6 py-6 text-base">
                  <Mail className="mr-2" size={18} /> Email Licensing
                </Button>
              </a>
            </div>
            <p className="text-[11px] text-blue-300/60 italic mt-10 max-w-3xl mx-auto leading-relaxed">
              © 2026 SALAR Technologies LLC · Wilsonville, Oregon · UEI U4TVNBSBUJP3 · CAGE 1AXR9. All patents pending.
              This page is provided for informational and licensing-inquiry purposes. Nothing on this page constitutes a
              binding offer, an enforceable license, or a representation regarding patent issuance. All licensing
              discussions are subject to mutual non-disclosure agreement.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Licensing;
